class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onPriceRangeLinkClick = this.onPriceRangeLinkClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    this.addEventListener('click', this.onPriceRangeLinkClick);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    FacetFiltersForm.scheduleDesktopStickyFilters();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    FacetFiltersForm.initDesktopStickyFilters();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    const targetPriceRangesElement = target.querySelector('.facets__price-ranges');
    const sourcePriceRangesElement = source.querySelector('.facets__price-ranges');

    if (sourcePriceRangesElement && targetPriceRangesElement) {
      targetPriceRangesElement.outerHTML = sourcePriceRangesElement.outerHTML;
    }

    const targetPriceElement = target.querySelector('.facets__price');
    const sourcePriceElement = source.querySelector('.facets__price');

    if (sourcePriceElement && targetPriceElement && !sourcePriceRangesElement) {
      targetPriceElement.outerHTML = sourcePriceElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static bindDesktopFilterToggle() {
    document.querySelectorAll('[data-desktop-filters-toggle]').forEach((button) => {
      if (button.dataset.filtersToggleBound === 'true') return;
      button.dataset.filtersToggleBound = 'true';

      button.addEventListener('click', () => {
        const layout = document.querySelector('[data-desktop-filters-layout]');
        if (!layout) return;

        const nextState = layout.dataset.filtersState === 'closed' ? 'open' : 'closed';
        layout.dataset.filtersState = nextState;

        document.querySelectorAll('[data-desktop-filters-toggle]').forEach((toggleButton) => {
          toggleButton.setAttribute('aria-expanded', nextState === 'open' ? 'true' : 'false');
          const label = toggleButton.querySelector('[data-filters-toggle-label]');
          if (label) label.textContent = nextState === 'open' ? 'Hide Filters' : 'Show Filters';
        });

        FacetFiltersForm.scheduleDesktopStickyFilters();
      });
    });
  }

  static initDesktopStickyFilters() {
    FacetFiltersForm.teardownDesktopStickyFilters();
  }

  static teardownDesktopStickyFilters() {
    if (FacetFiltersForm.desktopStickyRaf) {
      window.cancelAnimationFrame(FacetFiltersForm.desktopStickyRaf);
      FacetFiltersForm.desktopStickyRaf = null;
    }

    window.removeEventListener('scroll', FacetFiltersForm.handleDesktopStickyScroll);
    window.removeEventListener('resize', FacetFiltersForm.handleDesktopStickyResize);
    FacetFiltersForm.desktopStickyMediaQuery?.removeEventListener(
      'change',
      FacetFiltersForm.handleDesktopStickyViewportChange
    );
    FacetFiltersForm.desktopStickyResizeObserver?.disconnect();

    document.querySelectorAll('.facets-wrapper--js-sticky').forEach((aside) => {
      aside.classList.remove('facets-wrapper--js-sticky');
      aside.style.removeProperty('--collection-facets-sticky-height');
      aside.style.removeProperty('--collection-facets-sticky-top');
      aside.style.removeProperty('--collection-facets-sticky-left');
      aside.style.removeProperty('--collection-facets-sticky-width');
      aside.style.removeProperty('--collection-facets-sticky-panel-height');
    });

    document.querySelectorAll('.facets-container').forEach((container) => {
      container.classList.remove('is-sticky-fixed', 'is-sticky-bottom');
    });

    document.querySelectorAll('#FacetsWrapperDesktop').forEach((desktopWrapper) => {
      desktopWrapper.style.removeProperty('--collection-facets-desktop-max-height');
    });

    FacetFiltersForm.resetDesktopStickyFilters();

    FacetFiltersForm.desktopStickyMediaQuery = undefined;
    FacetFiltersForm.desktopStickyResizeObserver = null;
    FacetFiltersForm.desktopStickyElements = null;
  }

  static handleDesktopStickyScroll() {
    FacetFiltersForm.scheduleDesktopStickyFilters();
  }

  static handleDesktopStickyResize() {
    FacetFiltersForm.scheduleDesktopStickyFilters();
  }

  static handleDesktopStickyViewportChange() {
    FacetFiltersForm.scheduleDesktopStickyFilters();
  }

  static scheduleDesktopStickyFilters() {
    if (!FacetFiltersForm.desktopStickyElements) return;

    if (FacetFiltersForm.desktopStickyRaf) {
      window.cancelAnimationFrame(FacetFiltersForm.desktopStickyRaf);
    }

    FacetFiltersForm.desktopStickyRaf = window.requestAnimationFrame(() => {
      FacetFiltersForm.desktopStickyRaf = null;
      FacetFiltersForm.updateDesktopStickyFilters();
    });
  }

  static updateDesktopStickyFilters() {
    const elements = FacetFiltersForm.desktopStickyElements;
    if (!elements) return;

    const { layout, aside, facetsContainer, desktopWrapper, productGridContainer } = elements;
    const isDesktop = FacetFiltersForm.desktopStickyMediaQuery?.matches;
    const isOpen = layout.dataset.filtersState !== 'closed';

    if (!isDesktop || !isOpen) {
      FacetFiltersForm.resetDesktopStickyFilters();
      return;
    }

    facetsContainer.classList.remove('is-sticky-fixed', 'is-sticky-bottom');

    const stickyTop = FacetFiltersForm.getDesktopStickyTopOffset();
    const rootStyles = getComputedStyle(document.documentElement);
    const rem = parseFloat(rootStyles.fontSize) || 10;
    const availableHeight = Math.max(window.innerHeight - stickyTop - rem * 2, 0);
    const naturalHeight = facetsContainer.offsetHeight;
    const visibleHeight = Math.min(naturalHeight, availableHeight);
    const layoutRect = layout.getBoundingClientRect();
    const asideRect = aside.getBoundingClientRect();
    const facetsContainerRect = facetsContainer.getBoundingClientRect();
    const asideTop = asideRect.top + window.scrollY;
    const layoutBottom = Math.max(
      layoutRect.bottom + window.scrollY,
      productGridContainer.getBoundingClientRect().bottom + window.scrollY
    );
    const currentTop = window.scrollY + stickyTop;

    if (!naturalHeight || !visibleHeight || visibleHeight >= layoutRect.height) {
      FacetFiltersForm.resetDesktopStickyFilters();
      return;
    }

    aside.style.setProperty('--collection-facets-sticky-height', `${naturalHeight}px`);
    aside.style.setProperty('--collection-facets-sticky-top', `${stickyTop}px`);
    aside.style.setProperty('--collection-facets-sticky-left', `${facetsContainerRect.left}px`);
    aside.style.setProperty('--collection-facets-sticky-width', `${facetsContainerRect.width}px`);
    aside.style.setProperty('--collection-facets-sticky-panel-height', `${visibleHeight}px`);
    desktopWrapper.style.setProperty('--collection-facets-desktop-max-height', `${visibleHeight}px`);

    if (currentTop <= asideTop) {
      facetsContainer.classList.remove('is-sticky-fixed', 'is-sticky-bottom');
      return;
    }

    if (currentTop + visibleHeight >= layoutBottom) {
      facetsContainer.classList.remove('is-sticky-fixed');
      facetsContainer.classList.add('is-sticky-bottom');
      return;
    }

    facetsContainer.classList.remove('is-sticky-bottom');
    facetsContainer.classList.add('is-sticky-fixed');
  }

  static resetDesktopStickyFilters() {
    const elements = FacetFiltersForm.desktopStickyElements;
    if (!elements) return;

    const { aside, facetsContainer, desktopWrapper } = elements;

    facetsContainer.classList.remove('is-sticky-fixed', 'is-sticky-bottom');
    aside.style.removeProperty('--collection-facets-sticky-height');
    aside.style.removeProperty('--collection-facets-sticky-top');
    aside.style.removeProperty('--collection-facets-sticky-left');
    aside.style.removeProperty('--collection-facets-sticky-width');
    aside.style.removeProperty('--collection-facets-sticky-panel-height');
    desktopWrapper.style.removeProperty('--collection-facets-desktop-max-height');
  }

  static getDesktopStickyTopOffset() {
    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
    const announcementHeight = parseFloat(rootStyles.getPropertyValue('--announcement-bar-height')) || 0;
    const stickyHeaderHeight = document.querySelector('.section-header')?.getBoundingClientRect().height || 0;
    const rem = parseFloat(rootStyles.fontSize) || 10;

    return Math.max(headerHeight + announcementHeight, stickyHeaderHeight) + rem * 2;
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onPriceRangeLinkClick(event) {
    const link = event.target.closest('[data-price-range-link]');
    if (!link) return;

    event.preventDefault();
    const currentFilter = link.closest('.js-filter');
    currentFilter?.querySelectorAll('[data-price-range-link]').forEach((priceLink) => {
      const isActive = priceLink === link;
      priceLink.classList.toggle('is-active', isActive);
      if (isActive) {
        priceLink.setAttribute('aria-current', 'true');
      } else {
        priceLink.removeAttribute('aria-current');
      }
    });
    const url = link.href.indexOf('?') === -1 ? '' : link.href.slice(link.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.desktopStickyRaf = null;
FacetFiltersForm.desktopStickyElements = null;
FacetFiltersForm.desktopStickyResizeObserver = null;
FacetFiltersForm.desktopStickyMediaQuery = undefined;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.bindDesktopFilterToggle();
FacetFiltersForm.initDesktopStickyFilters();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = Array.from(this.querySelectorAll('input'));
    if (this.inputs.length < 2) return;

    this.inputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.inputs?.length ? this.inputs : Array.from(this.querySelectorAll('input'));
    if (inputs.length < 2) return;

    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
